
import { supabase } from '@/integrations/supabase/client';
import { AdminStats } from '@/utils/admin-helpers';
import { format, subDays, subWeeks, subMonths } from 'date-fns';

export interface EnhancedAdminStats extends AdminStats {
  weeklyGrowth: {
    clients: number;
    practitioners: number;
    bookings: number;
  };
  monthlyGrowth: {
    clients: number;
    practitioners: number;
    bookings: number;
  };
  conversionRate: number;
  avgBookingValue: number;
  topPerformingPractitioners: Array<{
    id: string;
    name: string;
    totalBookings: number;
    totalRevenue: number;
  }>;
}

/**
 * Fetches enhanced admin dashboard statistics
 */
export const getEnhancedAdminStats = async (): Promise<EnhancedAdminStats> => {
  // Get basic stats first
  const basicStats = await getBasicStats();
  
  // Get growth rates
  const weeklyGrowth = await getGrowthRates('week');
  const monthlyGrowth = await getGrowthRates('month');
  
  // Get conversion and performance metrics
  const conversionRate = await getConversionRate();
  const avgBookingValue = await getAverageBookingValue();
  const topPerformingPractitioners = await getTopPerformingPractitioners();

  return {
    ...basicStats,
    weeklyGrowth,
    monthlyGrowth,
    conversionRate,
    avgBookingValue,
    topPerformingPractitioners
  };
};

const getBasicStats = async (): Promise<AdminStats> => {
  // Get counts
  const { count: clientCount } = await supabase
    .from('clients')
    .select('*', { count: 'exact', head: true });

  const { count: practitionerCount } = await supabase
    .from('practitioners')
    .select('*', { count: 'exact', head: true });

  const { count: pendingPractitionerCount } = await supabase
    .from('practitioners')
    .select('*', { count: 'exact', head: true })
    .eq('profile_status', 'pending_review');

  const { count: bookingCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true });

  // Get recent data
  const { data: recentClients } = await supabase
    .from('clients')
    .select('id, first_name, last_name, created_at')
    .order('created_at', { ascending: false })
    .limit(5);

  const { data: recentPractitioners } = await supabase
    .from('practitioners')
    .select('id, first_name, last_name, profile_status, created_at')
    .order('created_at', { ascending: false })
    .limit(5);

  const { data: recentBookings } = await supabase
    .from('bookings')
    .select(`
      id, 
      start_time, 
      status, 
      price_at_booking,
      currency_at_booking,
      clients:client_id (first_name, last_name),
      practitioners:practitioner_id (first_name, last_name)
    `)
    .order('created_at', { ascending: false })
    .limit(5);

  return {
    clientCount: clientCount || 0,
    practitionerCount: practitionerCount || 0,
    pendingPractitionerCount: pendingPractitionerCount || 0,
    bookingCount: bookingCount || 0,
    recentClients: recentClients || [],
    recentPractitioners: recentPractitioners || [],
    recentBookings: recentBookings || []
  };
};

const getGrowthRates = async (period: 'week' | 'month') => {
  const dateSubtractor = period === 'week' ? subWeeks : subMonths;
  const periodStart = dateSubtractor(new Date(), 1);
  
  // Get current period counts
  const { count: currentClients } = await supabase
    .from('clients')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', periodStart.toISOString());

  const { count: currentPractitioners } = await supabase
    .from('practitioners')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', periodStart.toISOString());

  const { count: currentBookings } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', periodStart.toISOString());

  return {
    clients: currentClients || 0,
    practitioners: currentPractitioners || 0,
    bookings: currentBookings || 0
  };
};

const getConversionRate = async (): Promise<number> => {
  // Calculate conversion rate as completed bookings / total clients
  const { count: totalClients } = await supabase
    .from('clients')
    .select('*', { count: 'exact', head: true });

  const { count: completedBookings } = await supabase
    .from('bookings')
    .select('client_id', { count: 'exact', head: true })
    .eq('payment_status', 'Completed');

  if (!totalClients || totalClients === 0) return 0;
  
  return Math.round((completedBookings || 0) / totalClients * 100);
};

const getAverageBookingValue = async (): Promise<number> => {
  const { data, error } = await supabase
    .from('bookings')
    .select('price_at_booking')
    .eq('payment_status', 'Completed');

  if (error || !data || data.length === 0) return 0;

  const total = data.reduce((sum, booking) => 
    sum + (parseFloat(booking.price_at_booking?.toString() || '0')), 0
  );

  return Math.round(total / data.length);
};

const getTopPerformingPractitioners = async () => {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      practitioner_id,
      price_at_booking,
      practitioners!inner(first_name, last_name)
    `)
    .eq('payment_status', 'Completed');

  if (error || !data) return [];

  const practitionerStats: Record<string, {
    name: string;
    totalBookings: number;
    totalRevenue: number;
  }> = {};

  data.forEach(booking => {
    const id = booking.practitioner_id;
    const practitioner = booking.practitioners;
    const revenue = parseFloat(booking.price_at_booking?.toString() || '0');
    
    if (!practitionerStats[id]) {
      practitionerStats[id] = {
        name: `${practitioner?.first_name || ''} ${practitioner?.last_name || ''}`.trim() || 'Unknown',
        totalBookings: 0,
        totalRevenue: 0
      };
    }
    
    practitionerStats[id].totalBookings++;
    practitionerStats[id].totalRevenue += revenue;
  });

  return Object.entries(practitionerStats)
    .map(([id, stats]) => ({ id, ...stats }))
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 5);
};
